import type { ContactRole, Role } from 'bundles/backoffice/contacts/types';

export type DealType = 'sale' | 'lease' | 'saleAndLease' | 'buyerRep' | 'tenantRep' | 'other';

interface CategoryConfig {
  contactLabel?: string,
  header: string;
  required?: boolean;
  roleValues?: string[];
}

export interface Category {
  contactLabel: string,
  contactRoles: ContactRole[];
  header: string;
  required: boolean;
  roles: Role[];
}

export function groupByCategory(
  contactRoles: ContactRole[],
  roles: Role[],
  dealType: DealType,
  required?: boolean | ((contactLabel: string) => boolean),
): Category[] {
  let remainingRoles = [...roles];

  return getCategoryConfigs(dealType, required).map((config) => {
    const roleValues = config.roleValues || [];

    let groupRoles;
    if (roleValues.length > 0) {
      groupRoles = remainingRoles.filter(role => roleValues.some(value => value === role.value));
      remainingRoles = remainingRoles.filter(role => !groupRoles.includes(role));
    } else {
      groupRoles = remainingRoles;
    }

    return {
      contactLabel: config.contactLabel || 'contact',
      contactRoles: contactRoles.filter(contactRole => groupRoles.some(role => role.value === contactRole.role)),
      header: config.header || 'Contacts',
      required: config.required || false,
      roles: groupRoles,
    };
  });
}

function getCategoryConfigs(dealType: DealType, required?: boolean | ((contactLabel: string) => boolean)) {
  function buildGroupConfig(contactLabel, header, roleValues) {
    return {
      contactLabel,
      header,
      required: typeof required === 'function' ? required(contactLabel) : (required || false),
      roleValues: roleValues,
    };
  }

  const configs: CategoryConfig[] = [];

  if (dealType === 'sale' || dealType === 'saleAndLease') {
    configs.push(buildGroupConfig('seller', 'Seller', ['seller', 'seller_contact']));
    configs.push(buildGroupConfig('buyer', 'Buyer', ['buyer', 'buyer_contact']));
  }

  if (dealType === 'lease' || dealType === 'saleAndLease') {
    configs.push(buildGroupConfig('owner', 'Owner', ['owner_landlord', 'owner_representative', 'owner_user']));
    configs.push(buildGroupConfig('tenant', 'Tenant', ['tenant', 'tenant_contact']));
  }

  if (dealType === 'buyerRep') {
    configs.push(buildGroupConfig('buyer', 'Buyer', ['buyer', 'buyer_contact', 'client']));
    configs.push(buildGroupConfig('seller', 'Seller', ['seller', 'seller_contact']));
  }

  if (dealType === 'tenantRep') {
    configs.push(buildGroupConfig('tenant', 'Tenant', ['tenant', 'tenant_contact', 'client']));
    configs.push(buildGroupConfig('owner', 'Owner/Landlord', ['owner_landlord', 'owner_representative', 'owner_user']));
  }

  configs.push({ header: 'Other Contacts' });

  return configs;
}
